import {
  parsedErrorNeedsMotivation,
  requestUserMotivation,
} from './motivation';

const RETRYABLE_METHODS = new Set(['POST', 'PATCH', 'PUT']);

/**
 * How long a granted motivation is shared after the prompt settles, so
 * parallel bulk chunks whose 400s straggle in reuse one answer instead of
 * re-prompting. Kept short so a later, unrelated operation must motivate
 * again (motivations are audit records on the approval actions).
 */
const MOTIVATION_REUSE_MS = 10_000;

// ponytail: the reuse window is a time-based proxy for "same operation" —
// no batch/operation id reaches this fetch layer to match on.
let sharedMotivation: {
  promise: Promise<string | null>;
  // null while the prompt is open — an open prompt never expires, so slow
  // typists don't get a second dialog stacked on the first.
  expiresAt: number | null;
} | null = null;

function getSharedMotivation(): Promise<string | null> {
  if (
    sharedMotivation &&
    (sharedMotivation.expiresAt === null ||
      sharedMotivation.expiresAt > Date.now())
  ) {
    return sharedMotivation.promise;
  }
  const promise = requestUserMotivation();
  const entry: NonNullable<typeof sharedMotivation> = {
    promise,
    expiresAt: null,
  };
  sharedMotivation = entry;
  promise.then((motivation) => {
    if (sharedMotivation !== entry) {
      return;
    }
    // A decline must not be cached — the next 400 should prompt again.
    if (motivation === null) {
      sharedMotivation = null;
    } else {
      entry.expiresAt = Date.now() + MOTIVATION_REUSE_MS;
    }
  });
  return promise;
}

function hasMotivation(body: unknown): boolean {
  if (Array.isArray(body)) {
    return body.some(
      (row) =>
        typeof row === 'object' && row !== null && 'mt_motivation' in row
    );
  }
  return typeof body === 'object' && body !== null && 'mt_motivation' in body;
}

/**
 * Same merge shape as the legacy stack's buildRetryWithMotivation: the
 * backend reads `mt_motivation` per row, so arrays get it on every row.
 */
export function mergeMotivation(body: unknown, motivation: string): unknown {
  if (Array.isArray(body)) {
    return body.map((row) =>
      typeof row === 'object' && row !== null
        ? { ...row, mt_motivation: motivation }
        : row
    );
  }
  if (typeof body === 'object' && body !== null) {
    return { ...body, mt_motivation: motivation };
  }
  return body;
}

/**
 * Fetch wrapper for the construct client: when a mutation gets a 400 whose
 * body signals "motivation required" (backend error 1013 / `mt_motivation`
 * entries), prompt the user once and retry with `mt_motivation` merged into
 * the JSON body. A declined prompt lets the original 400 flow through, so
 * callers see the same error they do today. Runs only in the browser.
 */
export function createMotivationAwareFetch(
  baseFetch: typeof globalThis.fetch
): typeof globalThis.fetch {
  return async (
    input: Parameters<typeof globalThis.fetch>[0],
    init?: RequestInit
  ) => {
    const response = await baseFetch(input, init);

    const method = (init?.method ?? 'GET').toUpperCase();
    if (
      response.status !== 400 ||
      typeof window === 'undefined' ||
      typeof init?.body !== 'string' ||
      !RETRYABLE_METHODS.has(method)
    ) {
      return response;
    }

    let errorBody: unknown;
    try {
      errorBody = await response.clone().json();
    } catch {
      return response;
    }
    if (!parsedErrorNeedsMotivation(errorBody)) {
      return response;
    }

    let requestBody: unknown;
    try {
      requestBody = JSON.parse(init.body);
    } catch {
      return response;
    }
    // The body already carried a motivation and still 400'd — retrying the
    // same payload would loop. Surface the error instead.
    if (hasMotivation(requestBody)) {
      return response;
    }

    const motivation = await getSharedMotivation();
    if (motivation === null) {
      return response;
    }

    return baseFetch(input, {
      ...init,
      body: JSON.stringify(mergeMotivation(requestBody, motivation)),
    });
  };
}
